import React from "react";

/**
 * Custom vector icon set for FrazzleCRM Funnel Mapper.
 * All icons are hand-drawn 24x24 stroke SVGs sharing one visual language:
 * fill-none, stroke-currentColor, 1.7px width, round caps/joins.
 */

export type IconProps = {
  className?: string;
  size?: number;
};

function Svg({
  className,
  size = 20,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* ---------- Traffic ---------- */

export const FacebookAdsIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M14.5 8h-1.7c-1 0-1.8.8-1.8 1.8V21" />
    <path d="M8.5 13h5.5" />
  </Svg>
);

export const GoogleSheetsIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5V7.5L14 3z" />
    <path d="M14 3v4.5h4.5" />
    <path d="M8.5 12h7M8.5 15h7M8.5 18h7M12 12v6" />
  </Svg>
);

export const InstagramIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="16" height="16" rx="4.5" />
    <circle cx="12" cy="12" r="3.6" />
    <circle cx="16.8" cy="7.2" r="0.4" fill="currentColor" stroke="none" />
  </Svg>
);

export const GoogleAdsIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10.2 5.5 4.6 15.2a3 3 0 0 0 5.2 3l5.6-9.7a3 3 0 0 0-5.2-3Z" />
    <path d="M13.8 11.5l3.4 5.9" />
    <circle cx="6.4" cy="17.2" r="1.1" fill="currentColor" stroke="none" />
  </Svg>
);

export const YouTubeIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="6" width="18" height="12.5" rx="3.5" />
    <path d="M10.2 9.6v5.3l4.6-2.65-4.6-2.65Z" />
  </Svg>
);

export const TikTokIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13.5 4v10.8a3.6 3.6 0 1 1-3.6-3.6" />
    <path d="M13.5 5.5c.5 2.2 2.2 3.9 4.5 4.2" />
  </Svg>
);

export const EmailBlastIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6" y="6" width="15" height="11" rx="2" />
    <path d="m6.5 7.5 6 4.5 6-4.5" />
    <path d="M3 10h1.5M2 13h2.5M3.5 16H5" />
  </Svg>
);

export const SeoIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 5 5" />
    <path d="M7.5 12.5 10 10l1.8 1.8 2.7-2.7" />
    <path d="M14.5 9.1h-2.4m2.4 0v2.4" />
  </Svg>
);

export const DirectTrafficIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 4.5 19 10.8l-6 1.7-2.6 5.7L5 4.5Z" />
    <path d="m13.6 14 4.4 4.4" />
  </Svg>
);

/* ---------- Funnel pages ---------- */

const BrowserFrame = () => (
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M3 8.2h18" />
    <circle cx="5.9" cy="6.1" r="0.35" fill="currentColor" stroke="none" />
    <circle cx="8" cy="6.1" r="0.35" fill="currentColor" stroke="none" />
  </>
);

export const LandingPageIcon = (p: IconProps) => (
  <Svg {...p}>
    <BrowserFrame />
    <path d="M6.5 11.5h6M6.5 14h4" />
    <rect x="14.5" y="11.5" width="4" height="5" rx="1" />
  </Svg>
);

export const OptinPageIcon = (p: IconProps) => (
  <Svg {...p}>
    <BrowserFrame />
    <rect x="6.5" y="11" width="11" height="2.6" rx="1" />
    <path d="m9 16.8 1.3 1.3 2.6-2.6" />
  </Svg>
);

export const SalesPageIcon = (p: IconProps) => (
  <Svg {...p}>
    <BrowserFrame />
    <path d="M12 10.5v7" />
    <path d="M14.2 12c0-.9-1-1.5-2.2-1.5s-2.2.6-2.2 1.4c0 2.2 4.4 1 4.4 3.2 0 .8-1 1.4-2.2 1.4s-2.2-.6-2.2-1.5" />
  </Svg>
);

export const CheckoutIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3.5 4.5h2l2.3 10.5h10.4l2.3-8H6.4" />
    <circle cx="9" cy="19" r="1.4" />
    <circle cx="16.5" cy="19" r="1.4" />
  </Svg>
);

export const UpsellIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 19.5v-3.2M9.3 19.5v-6.7M14.6 19.5V9.3M19.9 19.5V5.8" />
    <path d="m4.5 10.5 5-4 4 2.5 5.5-5" />
    <path d="M19 4h-3.2M19 4v3.2" />
  </Svg>
);

export const DownsellIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12.8 4.2 4.2 12.8a2.4 2.4 0 0 0 0 3.4l3.6 3.6a2.4 2.4 0 0 0 3.4 0l8.6-8.6V4.2h-7Z" />
    <circle cx="16.2" cy="7.8" r="0.5" fill="currentColor" stroke="none" />
    <path d="M9.5 11v6M9.5 17l-2-2m2 2 2-2" />
  </Svg>
);

export const ThankYouIcon = (p: IconProps) => (
  <Svg {...p}>
    <BrowserFrame />
    <path d="M12 17.5s-4-2.2-4-4.7c0-1.3 1-2.3 2.2-2.3.8 0 1.5.4 1.8 1 .3-.6 1-1 1.8-1 1.2 0 2.2 1 2.2 2.3 0 2.5-4 4.7-4 4.7Z" />
  </Svg>
);

export const WebinarIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="18" height="12" rx="2.5" />
    <path d="M10.4 8.3v4.9l4.2-2.45-4.2-2.45Z" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </Svg>
);

export const BookingIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    <path d="m9.5 14.5 1.8 1.8 3.4-3.4" />
  </Svg>
);

export const SurveyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="5" y="4" width="14" height="17" rx="2.5" />
    <rect x="8" y="7.5" width="2.4" height="2.4" rx="0.6" />
    <path d="M13 8.7h3.5" />
    <rect x="8" y="13" width="2.4" height="2.4" rx="0.6" />
    <path d="M13 14.2h3.5" />
    <path d="m8.4 8.5.7.7 1.2-1.2" />
  </Svg>
);

export const MembersIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="9" r="3.2" />
    <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <rect x="15" y="11" width="6.5" height="5.5" rx="1.2" />
    <path d="M16.5 11V9.8a1.75 1.75 0 0 1 3.5 0V11" />
  </Svg>
);

export const PopupIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <rect x="7.5" y="9" width="9" height="6.5" rx="1.5" />
    <path d="m14.7 10.8 1.6 1.6m0-1.6-1.6 1.6" />
  </Svg>
);

export const WebinarRegIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="18" height="12" rx="2.5" />
    <path d="M6.5 8h6M6.5 11h4" />
    <path d="m13.5 10.2 1.7 1.7 3.3-3.4" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </Svg>
);

export const WebinarReplayIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="18" height="12" rx="2.5" />
    <path d="M15.2 9a3.4 3.4 0 1 0 .6 3.8" />
    <path d="M15.5 6.6v2.6H13" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </Svg>
);

/* ---------- Flows ---------- */

export const FlowIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="3" strokeDasharray="3.2 2.4" />
    <circle cx="8.2" cy="12" r="1.8" />
    <rect x="13.8" y="10.2" width="3.6" height="3.6" rx="1" />
    <path d="M10.3 12h3" />
  </Svg>
);

/* ---------- Automation (Go High Level style) ---------- */

export const InjectIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m3.5 20.5 4-4" />
    <path d="m7 13.5 3.5 3.5 7-7L14 6.5l-7 7Z" />
    <path d="m12.5 5 6.5 6.5" />
    <path d="m16.5 3.5 4 4" />
    <path d="m10.5 10 1.7 1.7M13 7.5l1.7 1.7" />
  </Svg>
);

export const TriggerLinkIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m11.5 13 4-4" />
    <path d="m13.8 6.7 1.2-1.2a3.4 3.4 0 0 1 4.8 4.8l-1.2 1.2" />
    <path d="m8.4 12.1-1.2 1.2a3.4 3.4 0 0 0 4.8 4.8l1.2-1.2" />
    <path d="M7.5 21 9 17.2 5.2 18l1.1-2.6" />
  </Svg>
);

export const AddToFlowIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="8.5" y="5" width="12.5" height="14" rx="3" strokeDasharray="3.2 2.4" />
    <circle cx="16" cy="12" r="1.8" />
    <path d="M2.5 12h9" />
    <path d="m8.8 9.3 2.7 2.7-2.7 2.7" />
  </Svg>
);

export const RemoveFromFlowIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="12.5" height="14" rx="3" strokeDasharray="3.2 2.4" />
    <circle cx="8" cy="12" r="1.8" />
    <path d="M11 12h9.5" />
    <path d="m17.8 9.3 2.7 2.7-2.7 2.7" />
  </Svg>
);

export const ApiCallIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m8.5 8-4.5 4 4.5 4M15.5 8l4.5 4-4.5 4" />
    <path d="M13.2 5.5l-2.4 13" />
  </Svg>
);

export const TriggerIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13 3 5 13.5h5.5L10 21l8-10.5h-5.5L13 3Z" />
  </Svg>
);

export const SendEmailIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 3.5 3.5 10.2l6.3 2.9L21 3.5Z" />
    <path d="M21 3.5 14.5 20l-4.7-6.9L21 3.5Z" />
  </Svg>
);

export const SendSmsIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 11.5c0 4-3.8 7-8.5 7-1 0-2-.14-2.9-.4L4 19.5l1.2-3.6c-1.1-1.2-1.7-2.7-1.7-4.4 0-4 3.8-7 8.5-7s8.5 3 8.5 7Z" />
    <path d="M8.3 11.5h.01M12 11.5h.01M15.7 11.5h.01" strokeWidth={2.4} />
  </Svg>
);

export const WaitIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 3.5h10M7 20.5h10" />
    <path d="M8 3.5v2.2c0 2.8 4 4.3 4 6.3s-4 3.5-4 6.3v2.2M16 3.5v2.2c0 2.8-4 4.3-4 6.3s4 3.5 4 6.3v2.2" />
  </Svg>
);

export const DripIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5c3.2 4 5.5 6.9 5.5 9.9a5.5 5.5 0 1 1-11 0c0-3 2.3-5.9 5.5-9.9Z" />
    <path d="M9.5 13.6a2.6 2.6 0 0 0 2 2.6" />
  </Svg>
);

export const ConditionIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4v5" />
    <path d="M12 9c0 3-6 3.5-6 7v1.5M12 9c0 3 6 3.5 6 7v1.5" />
    <path d="m4.2 15.7 1.8 1.8 1.8-1.8M16.2 15.7l1.8 1.8 1.8-1.8" />
    <circle cx="12" cy="4" r="1.1" fill="currentColor" stroke="none" />
  </Svg>
);

export const SplitTestIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5" width="8" height="14" rx="2" />
    <rect x="13" y="5" width="8" height="14" rx="2" />
    <path d="M5.5 15.5 7 9.5l1.5 6M6 13.5h2" />
    <path d="M15.5 9.5h1.8a1.5 1.5 0 0 1 0 3h-1.8m0-3v6m0-3h2a1.5 1.5 0 0 1 0 3h-2" />
  </Svg>
);

export const TagIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11.8 4 4 11.8a2.4 2.4 0 0 0 0 3.4l4.8 4.8a2.4 2.4 0 0 0 3.4 0l7.8-7.8V4h-8.2Z" />
    <circle cx="16.2" cy="7.8" r="0.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const AddTagIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11.8 4 4 11.8a2.4 2.4 0 0 0 0 3.4l4.8 4.8a2.4 2.4 0 0 0 3.4 0l7.8-7.8V4h-8.2Z" />
    <circle cx="16.2" cy="7.8" r="0.5" fill="currentColor" stroke="none" />
    <path d="M9.7 12.4v4.4M7.5 14.6h4.4" />
  </Svg>
);

export const RemoveTagIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11.8 4 4 11.8a2.4 2.4 0 0 0 0 3.4l4.8 4.8a2.4 2.4 0 0 0 3.4 0l7.8-7.8V4h-8.2Z" />
    <circle cx="16.2" cy="7.8" r="0.5" fill="currentColor" stroke="none" />
    <path d="M7.5 14.6h4.4" />
  </Svg>
);

export const PipelineIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="4" width="4.6" height="16" rx="1.4" />
    <rect x="9.7" y="4" width="4.6" height="11" rx="1.4" />
    <rect x="15.9" y="4" width="4.6" height="7" rx="1.4" />
  </Svg>
);

export const WebhookIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 6.5a3.2 3.2 0 1 1 4.8 2.8L12 14" />
    <path d="M8.5 11.2a3.2 3.2 0 1 0 3.5 5.3h5" />
    <path d="M17 16.5a3.2 3.2 0 1 0-2.8-4.7L12 6.5" />
    <circle cx="12" cy="14" r="1" fill="currentColor" stroke="none" />
  </Svg>
);

export const NotificationIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 10a6 6 0 0 0-12 0c0 5-2 6-2 6h16s-2-1-2-6Z" />
    <path d="M10 19.5a2.2 2.2 0 0 0 4 0" />
  </Svg>
);

/** Ringing bell — Internal Notification (bell + dot ≠ DND's plain bell). */
export const NotifyIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M17.5 10.5a5.5 5.5 0 0 0-11 0c0 4.6-1.8 5.5-1.8 5.5h14.6s-1.8-.9-1.8-5.5Z" />
    <path d="M10.2 19.3a2 2 0 0 0 3.6 0" />
    <circle cx="18.6" cy="5" r="2" fill="currentColor" stroke="none" />
  </Svg>
);

export const TaskIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="16" height="16" rx="3" />
    <path d="m8 12.2 2.6 2.6 5.4-5.6" />
  </Svg>
);

export const CallIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8.4 4.2 5.9 3.5c-1 .3-2.4 1.7-2.4 3.3C3.5 13 11 20.5 17.2 20.5c1.6 0 3-1.4 3.3-2.4l-.7-2.5-3.9-1.6-1.7 1.7c-2.3-.8-4.1-2.6-4.9-4.9L11 9.1 8.4 4.2Z" />
  </Svg>
);

export const GoalIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </Svg>
);

/* ---------- Outcomes ---------- */

export const NewLeadIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="8.5" r="3.5" />
    <path d="M4.5 20c0-3.3 2.7-5.5 6-5.5 1 0 2 .2 2.8.6" />
    <path d="M17.5 14.5v6M14.5 17.5h6" />
  </Svg>
);

export const AppointmentIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7v5l3.5 2" />
  </Svg>
);

export const PurchaseIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3 9.8h18" />
    <path d="M6.5 14.8h4" />
  </Svg>
);

export const DealWonIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M8 4h8v6a4 4 0 0 1-8 0V4Z" />
    <path d="M8 5.5H5a3 3 0 0 0 3 4.4M16 5.5h3a3 3 0 0 1-3 4.4" />
    <path d="M12 14v3M9 20h6M10 17h4l.6 3H9.4l.6-3Z" />
  </Svg>
);

/* ---------- Utility ---------- */

export const NoteIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8l-5 5h-8a2 2 0 0 1-2-2v-11Z" />
    <path d="M19.5 14.5H16a1.5 1.5 0 0 0-1.5 1.5v3.5" />
  </Svg>
);

export const LinkIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 14.5 14 10" />
    <path d="m12.8 7.2 1.7-1.7a3.5 3.5 0 0 1 5 5l-1.7 1.7" />
    <path d="m11.2 16.8-1.7 1.7a3.5 3.5 0 0 1-5-5l1.7-1.7" />
  </Svg>
);

export const GoToIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 12h12" />
    <path d="M12 6l6 6-6 6" />
    <circle cx="20" cy="12" r="2" fill="#fff" />
  </Svg>
);

export const EndIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8" />
    <rect x="9" y="9" width="6" height="6" rx="1" fill="#fff" />
  </Svg>
);

export const CustomIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7.5h16M4 16.5h16" />
    <circle cx="9.5" cy="7.5" r="2" fill="#fff" />
    <circle cx="14.5" cy="16.5" r="2" fill="#fff" />
  </Svg>
);

export const FormIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="3.5" width="16" height="17" rx="2.5" />
    <path d="M7.5 8h9M7.5 12h9M7.5 16h5" />
  </Svg>
);

/* ---------- annotation shapes ---------- */

export const TextAnnoIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 6.5V4h14v2.5" />
    <path d="M12 4v16" />
    <path d="M9 20h6" />
  </Svg>
);

export const ArrowAnnoIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 19 19 5" />
    <path d="M11.5 4.5H19.5V12.5" />
  </Svg>
);

export const LineAnnoIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20 20 4" />
  </Svg>
);

export const CircleAnnoIcon = (p: IconProps) => (
  <Svg {...p}>
    <ellipse cx="12" cy="12" rx="8.5" ry="6.5" />
  </Svg>
);

export const RectAnnoIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="6" width="16" height="12" rx="2" />
  </Svg>
);

/* ---------- UI chrome icons ---------- */

export const PlusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const TrashIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 6.5h15M9.5 6.5v-2h5v2M6.5 6.5 7.3 20h9.4l.8-13.5" />
    <path d="M10 10.5v6M14 10.5v6" />
  </Svg>
);

export const DuplicateIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 4.5H6.5a2 2 0 0 0-2 2V16" />
  </Svg>
);

export const ExportIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 15V4M12 4 8.5 7.5M12 4l3.5 3.5" />
    <path d="M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" />
  </Svg>
);

export const ImportIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4v11M12 15l-3.5-3.5M12 15l3.5-3.5" />
    <path d="M4.5 15v3a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" />
  </Svg>
);

export const ExternalIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4.5h5.5V10" />
    <path d="M19.5 4.5 11 13" />
    <path d="M19.5 13.5v4a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h4" />
  </Svg>
);

export const BookIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 5.5A2.5 2.5 0 0 1 7 3h12.5v15H7a2.5 2.5 0 0 0-2.5 2.5v-15Z" />
    <path d="M4.5 20.5A2.5 2.5 0 0 1 7 18h12.5" />
  </Svg>
);

export const SearchIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 5 5" />
  </Svg>
);

export const ChevronIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m8 10 4 4 4-4" />
  </Svg>
);

export const CloseIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Svg>
);

/* ---------- UI state (contact detail; replaces emoji) ---------- */

export const CheckIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Svg>
);

/** Bell with a slash — Do Not Disturb ON (NotificationIcon = DND off). */
export const BellOffIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 10a6 6 0 0 0-8.2-5.6M6.3 7.7A6 6 0 0 0 6 10c0 5-2 6-2 6h12" />
    <path d="M10 19.5a2.2 2.2 0 0 0 4 0" />
    <path d="M3.5 3.5l17 17" />
  </Svg>
);

export const AlertIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 4.5 2.8 20h18.4L12 4.5Z" />
    <path d="M12 10v4" />
    <path d="M12 17.2h.01" />
  </Svg>
);

export const HelpIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.7 9.5a2.4 2.4 0 0 1 4.6.8c0 1.6-2.3 2-2.3 3.4" />
    <path d="M12 17.2h.01" />
  </Svg>
);

export const StarIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 4 2.5 5.2 5.5.8-4 3.9 1 5.6-5-2.7-5 2.7 1-5.6-4-3.9 5.5-.8L12 4Z" />
  </Svg>
);

export const ArrowRightIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 12h15" />
    <path d="m13.5 6 6 6-6 6" />
  </Svg>
);

export const ArrowLeftIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M19.5 12h-15" />
    <path d="m10.5 6-6 6 6 6" />
  </Svg>
);

/** WhatsApp — handset inside a speech bubble, drawn in our stroke language. */
export const WhatsAppIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 11.7a8.5 8.5 0 0 1-12.6 7.5L3.5 20.5l1.4-4.3A8.5 8.5 0 1 1 20.5 11.7Z" />
    <path d="M9.3 9c.2-.5.5-.5.8-.5h.5l.8 1.9-.8.8a4.7 4.7 0 0 0 2.4 2.4l.8-.8 1.9.8v.5c0 .3 0 .6-.5.8-.5.3-1.3.4-2.4 0a7.3 7.3 0 0 1-3.6-3.6c-.4-1.1-.3-1.9 0-2.4Z" />
  </Svg>
);

export const MoreIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="5.5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="18.5" cy="12" r="1.2" />
  </Svg>
);

export const BuildingIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20.5h16" />
    <path d="M6 20.5V5.5A1.5 1.5 0 0 1 7.5 4h6A1.5 1.5 0 0 1 15 5.5v15" />
    <path d="M15 10h2.5A1.5 1.5 0 0 1 19 11.5v9" />
    <path d="M8.8 8h3.4M8.8 11.5h3.4M8.8 15h3.4" />
  </Svg>
);

export const GlobeIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5a13 13 0 0 1 0 17a13 13 0 0 1 0-17Z" />
  </Svg>
);

export const UndoIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 7 4.5 11.5 9 16" />
    <path d="M5 11.5h7.5a6 6 0 0 1 6 6" />
  </Svg>
);

export const RedoIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m15 7 4.5 4.5L15 16" />
    <path d="M19 11.5h-7.5a6 6 0 0 0-6 6" />
  </Svg>
);

export const HistoryStackIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5 7 7-3 7 3-7 3-7-3Z" />
    <path d="m5 11 7 3 7-3" />
    <path d="m5 15 7 3 7-3" />
  </Svg>
);

/* ---------- Providers ---------- */

export const StripeIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M15 8.6c-.8-.6-2-1-3.1-1-1.6 0-2.7.8-2.7 2 0 3 6.1 1.6 6.1 4.7 0 1.3-1.2 2.1-2.9 2.1-1.3 0-2.6-.5-3.4-1.1" />
  </Svg>
);

export const GhlIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 12.5 12 5l8 7.5" />
    <path d="M6.5 11v7.5h11V11" />
    <path d="M12 18.5v-4" />
    <circle cx="12" cy="11.5" r="1.4" />
  </Svg>
);

export const EwebinarIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="18" height="12" rx="2.2" />
    <path d="M10.4 8v5l4.2-2.5-4.2-2.5Z" />
    <path d="M8.5 20h7" />
    <circle cx="18" cy="7.5" r="0.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const MainAppIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="4" width="17" height="16" rx="2.5" />
    <path d="M3.5 8.5h17" />
    <circle cx="6.3" cy="6.2" r="0.45" fill="currentColor" stroke="none" />
    <circle cx="8.4" cy="6.2" r="0.45" fill="currentColor" stroke="none" />
    <path d="M7 12.5h4.5M7 15.8h7" />
    <path d="m15.5 12 2 2-2 2" />
  </Svg>
);

export const VirtualOfficeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 20V7.5a1.5 1.5 0 0 1 1.5-1.5H13a1.5 1.5 0 0 1 1.5 1.5V20" />
    <path d="M14.5 11H18a1.5 1.5 0 0 1 1.5 1.5V20" />
    <path d="M3.5 20h17" />
    <path d="M8 9.5h3.5M8 12.8h3.5M8 16h3.5" />
    <circle cx="17" cy="15" r="0.5" fill="currentColor" stroke="none" />
  </Svg>
);

export const WebsiteIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.4 2.2 3.6 5.2 3.6 8.5s-1.2 6.3-3.6 8.5c-2.4-2.2-3.6-5.2-3.6-8.5s1.2-6.3 3.6-8.5Z" />
  </Svg>
);

export const ZapierIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13.5 3.5 6 13.5h5l-1 7 7.5-10h-5l1-7Z" />
  </Svg>
);

export const CalendlyIcon = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
    <path d="M4 10h16M8.5 3.5v3.5M15.5 3.5v3.5" />
    <circle cx="12" cy="15" r="2.4" />
  </Svg>
);

export const ShopifyIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.5 8 5 20l7 1.5L19 20 17.5 8h-11Z" />
    <path d="M9 8V6.7A3 3 0 0 1 12 4a3 3 0 0 1 3 2.7V8" />
  </Svg>
);

export const SlackIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 4.5v6M15 13.5v6M4.5 15h6M13.5 9h6" />
    <circle cx="9" cy="4.5" r="1.6" />
    <circle cx="15" cy="19.5" r="1.6" />
    <circle cx="4.5" cy="15" r="1.6" />
    <circle cx="19.5" cy="9" r="1.6" />
  </Svg>
);

export const TwilioIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="9.5" cy="9.5" r="1.5" />
    <circle cx="14.5" cy="9.5" r="1.5" />
    <circle cx="9.5" cy="14.5" r="1.5" />
    <circle cx="14.5" cy="14.5" r="1.5" />
  </Svg>
);

export const QuickbooksIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.5 7.5a3.6 3.6 0 0 0 0 7.2h1.2" />
    <path d="M14.5 16.5a3.6 3.6 0 0 0 0-7.2h-1.2" />
    <path d="M10.7 5.5v9.2M13.3 9.3v9.2" />
  </Svg>
);

export const PlugIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 3.5V8M15 3.5V8" />
    <path d="M6.5 8h11v3a5.5 5.5 0 0 1-5.5 5.5A5.5 5.5 0 0 1 6.5 11V8Z" />
    <path d="M12 16.5v4" />
  </Svg>
);

export const TrafficIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="5.5" cy="6.5" r="2.2" />
    <circle cx="18.5" cy="6.5" r="2.2" />
    <circle cx="12" cy="17.5" r="2.2" />
    <path d="M7.5 7.6c2 1.6 2.6 4.7 3.2 7.7M16.5 7.6c-2 1.6-2.6 4.7-3.2 7.7" strokeDasharray="2.4 2" />
  </Svg>
);

/**
 * Sales Scripts: the call headset (container + category), plus per-node-type
 * glyphs — speech bubble (statement), bolt (rep action), info (context).
 */
export const HeadsetIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4.5 14v-2.5a7.5 7.5 0 0 1 15 0V14" />
    <rect x="3.5" y="13" width="3.8" height="5.5" rx="1.6" />
    <rect x="16.7" y="13" width="3.8" height="5.5" rx="1.6" />
    <path d="M18.6 18.5v.4a2.6 2.6 0 0 1-2.6 2.6h-3.2" />
  </Svg>
);

export const SpeechIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6.5 4.5h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H11l-4.5 4v-4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2Z" />
    <path d="M8.5 8.3h7M8.5 11h4.5" />
  </Svg>
);

export const BoltIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12.8 2.8 5.2 13.2h4.9l-1 8 7.7-10.4h-4.9l.9-8Z" />
  </Svg>
);

export const InfoIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11.2v5" />
    <path d="M12 7.8h.01" />
  </Svg>
);

/**
 * FrazzleCRM logo: a funnel with flowing nodes.
 */
export const FrazzleLogo = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="frzl-g" x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#6366f1" />
        <stop offset="1" stopColor="#a855f7" />
      </linearGradient>
    </defs>
    <path
      d="M5 5h22a1.6 1.6 0 0 1 1.25 2.6L20 17.5V25a1.6 1.6 0 0 1-.85 1.4l-4.8 2.4A1.6 1.6 0 0 1 12 27.4v-9.9L3.75 7.6A1.6 1.6 0 0 1 5 5Z"
      fill="url(#frzl-g)"
    />
    <circle cx="16" cy="10" r="1.9" fill="#fff" />
    <circle cx="16" cy="16.5" r="1.4" fill="#fff" opacity="0.85" />
    <circle cx="16" cy="22" r="1" fill="#fff" opacity="0.7" />
    <path d="M16 11.9v3.2m0 2.8v3.1" stroke="#fff" strokeWidth="1" opacity="0.7" />
  </svg>
);
